/**
 * Snapshot builder — projects FlashkaState into a SyncSnapshot for the
 * sync engine.
 *
 * After Phase 5.3b every entity (cards + deckProperties + folders +
 * settings + …) lives in its own auto-generated slice on the store, so
 * `buildSnapshot` is mostly a thin pass-through: it ALIASES each
 * stateKey from the store onto the snapshot. The engine reads cards as
 * `Record<deckId, Record<cardId, CardProps>>` (nested-record shape)
 * directly — no Map→Array conversion needed.
 *
 * The previous per-deck memoization cache existed to keep `cards[deckId]`
 * reference-equal for unchanged decks so detectDirty could short-circuit.
 * That short-circuit is now intrinsic to the nested-record state shape:
 * Zustand only re-spreads the parent bucket that was mutated.
 */

import type { SyncSnapshot } from '@flashka/shared/sync-engine/framework/sync-engine-types';

import { clientEntityByStateKey } from '../internal/entities/client-entities';
import type { FlashkaState } from '../internal/store/flashka-store';

/**
 * Build a SyncSnapshot from FlashkaState.
 */
export function buildSnapshot(
  state: FlashkaState,
  serverTimestamps: Record<string, string>,
): SyncSnapshot {
  // All schema-registered entities (array, collection, value, record) go
  // through the registry. Adding a new entity to entitySchema automatically
  // exposes it to detectDirtyChanges — no edits needed here.
  //
  // The named properties (folders, globalSrSettings, userInfo,
  // deckProperties) are kept explicit to satisfy SyncSnapshot's
  // required-key typing.
  const entityState: Record<string, unknown> = {};
  const stateView = state as unknown as Record<string, unknown>;
  for (const entity of Object.values(clientEntityByStateKey)) {
    entityState[entity.stateKey] = stateView[entity.stateKey];
  }

  return {
    deckProperties: state.deckProperties,
    folders: state.folders,
    globalSrSettings: state.globalSrSettings,
    userInfo: state.userInfo,
    ...entityState,
    serverTimestamps,
  };
}

/**
 * Clear the per-deck memoization cache.
 *
 * Retained as a no-op now that the cache itself is gone — keeps callers
 * (test setup, user-switch logic) compiling. Removed in the follow-up
 * after consumers are scrubbed.
 */
export function resetSnapshotCache(): void {
  /* no-op */
}
